import Vue from "vue";
import { z } from "zod";

export const nativeAuthSessionSchema = z.object({
  userId: z.string().min(1),
  vinculo: z.string().min(1),
  accessToken: z.string().min(1),
  expiresAt: z.number().int().positive()
});

type Token = { access_token?: string; exp?: number };
type WebSessionSnapshot = { userId: unknown; vinculo: unknown; rawToken: string | null };
type SessionEventBus = { $on(event: string, listener: (payload: unknown) => void): void };
const sessionRequestSchema = z.object({
  requestId: z.string().min(1),
  userId: z.string().min(1),
  vinculo: z.string().min(1)
});
let token: Token | null = null;
let identity: { userId: string; vinculo: string } | null = null;
let delivery = Promise.resolve();

function deliverNativeAuthCommand(command: () => unknown): Promise<void> {
  delivery = delivery
    .then(async () => {
      const target = Vue.prototype.$native;
      if (target) await target.postMessage(JSON.stringify(command()));
    })
    .catch(() => {
      // Never log the command, token, or bridge error payload.
      console.warn("[auth] native session delivery failed");
    });
  return delivery;
}

// Ordered delivery prevents a delayed logout from clearing a newer session.
export function publishNativeAuthSession(): Promise<void> {
  const parsed = nativeAuthSessionSchema.safeParse({
    ...identity,
    accessToken: token?.access_token,
    expiresAt: token?.exp
  });
  const command = parsed.success ? { type: "auth:setSession", payload: parsed.data } : { type: "auth:clearSession" };
  return deliverNativeAuthCommand(() => command);
}

export function registerNativeAuthSessionRecovery(bus: SessionEventBus, readSession: () => WebSessionSnapshot): void {
  bus.$on("auth:requestSession", payload => {
    let request: z.infer<typeof sessionRequestSchema>;
    try {
      request = sessionRequestSchema.parse(typeof payload === "string" ? JSON.parse(payload) : payload);
    } catch {
      return;
    }
    void deliverNativeAuthCommand(() => {
      let session: z.infer<typeof nativeAuthSessionSchema> | null = null;
      try {
        const snapshot = readSession();
        const currentToken: Token | null = snapshot.rawToken ? JSON.parse(snapshot.rawToken) : null;
        const parsed = nativeAuthSessionSchema.safeParse({
          userId: snapshot.userId ? String(snapshot.userId) : "",
          vinculo: snapshot.vinculo ? String(snapshot.vinculo) : "",
          accessToken: currentToken?.access_token,
          expiresAt: currentToken?.exp
        });
        if (
          parsed.success &&
          parsed.data.expiresAt > Date.now() &&
          parsed.data.userId === request.userId &&
          parsed.data.vinculo === request.vinculo
        ) {
          session = parsed.data;
          token = currentToken;
          identity = { userId: session.userId, vinculo: session.vinculo };
        }
      } catch {
        session = null;
      }
      return { type: "auth:sessionResult", payload: { requestId: request.requestId, session } };
    });
  });
}

export function updateNativeAuthToken(value: Token): void {
  token = value;
  void publishNativeAuthSession();
}

export function setNativeAuthIdentity(userId: unknown, vinculo: unknown): void {
  identity = userId && vinculo ? { userId: String(userId), vinculo: String(vinculo) } : null;
  void publishNativeAuthSession();
}

export function clearNativeAuthSession(): void {
  token = null;
  identity = null;
  void publishNativeAuthSession();
}

// Called once by the PWA at startup; Flutter never reads its localStorage.
export function restoreNativeAuthSession(userId: unknown, vinculo: unknown, raw: string | null): void {
  try {
    token = raw ? JSON.parse(raw) : null;
  } catch {
    token = null;
  }
  setNativeAuthIdentity(userId, vinculo);
}
